<script setup lang="ts">
import { computed } from 'vue';
import { Avatar } from '@boqnet/ui';
import i18n from '@/plugins/vue-i18n';
import PinIcon from '@/components/Brand/PinIcon.vue';
import type { UserOrganisation } from '@/types/api/auth.types';

const { t } = i18n.global;

const props = defineProps<{
  org: Partial<UserOrganisation>;
  isSelected: boolean;
}>();

const emit = defineEmits<{
  (e: 'select'): void;
  (e: 'set-primary'): void;
}>();

const pinTitle = computed(() =>
  props.org.primary
    ? t('navigation.organisationSwitcher.primaryOrganisation')
    : t('navigation.organisationSwitcher.setAsPrimary'),
);
</script>

<template>
  <div
    class="group flex cursor-pointer items-center gap-3 border-l-4 py-2 pr-2 pl-3 transition-colors"
    :class="isSelected ? 'border-blue-700 bg-blue-50' : 'border-transparent hover:bg-gray-50'"
    :data-cy="`organisation-switcher-row-${org.id}`"
    @click="emit('select')"
  >
    <Avatar :label="org.name" :src="org.logoUrl || undefined" size="md" class="shrink-0" />

    <span
      class="type-body-1 min-w-0 flex-1 wrap-break-word"
      :class="isSelected ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'"
    >
      {{ org.name }}
    </span>

    <button
      type="button"
      class="shrink-0 rounded-md p-1.5 transition-opacity"
      :class="
        org.primary
          ? 'text-blue-700 opacity-100'
          : 'text-gray-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100'
      "
      :title="pinTitle"
      :aria-label="pinTitle"
      data-cy="organisation-switcher-set-primary"
      @click.stop="emit('set-primary')"
    >
      <PinIcon :fill="org.primary ? 'currentColor' : 'none'" />
    </button>
  </div>
</template>
